import { z, type ZodTypeAny } from "zod";
import { extendZodWithOpenApi, type ZodOpenApiResponseObject } from "zod-openapi";
import { errorResponse, formBody, ind, jsonBody, privateApi, successResponse, tagPaths, unref } from "../util.ts";
import { InvoiceStatusEnum, KycProcessStatusEnum } from "./billingEnums.ts";
import {
    billing_account_id,
    datetime_epoch_s,
    datetime_z,
    int,
    int_or_string,
    json_string,
    simpleSuccessResponse,
    user_id,
    uuid,
} from "./common.ts";

extendZodWithOpenApi(z);

const LinkMethodId = z
    .string()
    .describe("Payment link method ID")
    .openapi({ ref: "LinkMethodId", example: "duitku::BK" });

const ProcessorMethodId = z
    .string()
    .describe("Payment processor method ID")
    .openapi({ ref: "ProcessorMethodId", example: "omise_creditcard" });

export const BillingAdditionalData = z
    .object({ link_methods: z.array(LinkMethodId) })
    .describe("JSON stored in the `additional_data` field of the billing account")
    .openapi({ ref: "BillingAdditionalData" });

const StripeProcessorData = z
    .object({
        id: z.string(),
        object: z.string(),
        allow_redisplay: z.string(),
        billing_details: z
            .object({
                address: z.object({
                    city: z.string().nullable(),
                    country: z.string().nullable(),
                    line1: z.string().nullable(),
                    line2: z.string().nullable(),
                    postal_code: z.string().nullable(),
                    state: z.string().nullable(),
                }),
                email: z.string().nullable(),
                // XXX: Backend should probably not allow this to be nullable (but it can be unless the frontend explicitly sends it)
                name: z.string().nullable(),
                phone: z.string().nullable(),
            })
            // TODO: are all fields here really optional?
            .partial()
            .passthrough(),
        card: z
            .object({
                brand: z.string(),
                checks: z
                    .object({
                        address_line1_check: z.string().nullable(), // TODO might not be string?
                        address_postal_code_check: z.string().nullable(), // TODO might not be string?
                        cvc_check: z.string().nullable(),
                    })
                    .passthrough(),
                country: z.string(),
                display_brand: z.string(),
                exp_month: int,
                exp_year: int,
                fingerprint: z.string(),
                funding: z.string(),
                generated_from: z.string().nullable(), // TODO might not be string?
                last4: z.string(),
                networks: z.object({
                    available: z.array(z.string()),
                    preferred: z.string().nullable(),
                }),
                three_d_secure_usage: z.object({ supported: z.boolean() }),
                wallet: z.union([z.string(), z.object({}).passthrough()]).nullable(),
            })
            // TODO: are all fields here really optional?
            .partial()
            .passthrough(),

        street1: z.string().nullable(),
        street2: z.string().nullable(),
        city: z.string().nullable(),
        state: z.string().nullable(),
        postal_code: z.string().nullable(),
        country: z.string().nullable(),
        phone_number: z.string().nullable(),
        financing: z.string(),
        bank: z.string(),
        brand: z.string(),
        fingerprint: z.string(),
        first_digits: z.string().nullable(),
        last_digits: z.string().nullable(),
        name: z.string(),
        expiration_month: int,
        expiration_year: int,
        security_code_check: z.boolean(),
        tokenization_method: z.string().nullable(),
        created_at: datetime_z,

        created: int,
        customer: z.string(),
        deleted: z.boolean(),
        livemode: z.boolean(),
        location: z.string(),
        metadata: z.object({}).passthrough(),
        type: z.string(),
    })
    .passthrough()
    // TODO: are all fields here really optional?
    .partial()
    .optional();

const PaypalVaultProcessorData = z
    .object({
        id: z.string(),
        customer: z
            .object({
                id: z.string(),
                merchant_customer_id: z.string().optional(),
            })
            .partial(),
        payment_source: z.object({
            paypal: z.object({
                account_status: z.string().optional(),
                app_switch_eligibility: z.boolean().optional(),
                stored_credential: z
                    .object({
                        payment_initiator: z.string(),
                        usage: z.string(),
                    })
                    .optional(),
                description: z.string().optional(),
                shipping: z
                    .object({
                        name: z.object({
                            full_name: z.string(),
                        }),
                        address: z.object({
                            address_line_1: z.string(),
                            address_line_2: z.string().optional(),
                            admin_area_2: z.string().optional(),
                            admin_area_1: z.string(),
                            postal_code: z.string(),
                            country_code: z.string(),
                        }),
                    })
                    .optional(),
                usage_pattern: z.string().optional(),
                usage_type: z.string().optional(),
                customer_type: z.string().optional(),
                email_address: z.string().email(),
                name: z.object({
                    given_name: z.string(),
                    surname: z.string(),
                    full_name: z.string().optional(),
                }),
                phone: z
                    .object({
                        phone_number: z.object({
                            country_code: z.string(),
                            national_number: z.string(),
                        }),
                    })
                    .optional(),
                account_id: z.string().optional(),
                payer_id: z.string().optional(),
                address: z
                    .object({
                        address_line_1: z.string().optional(),
                        address_line_2: z.string().optional(),
                        admin_area_1: z.string().optional(),
                        admin_area_2: z.string().optional(),
                        postal_code: z.string().optional(),
                        country_code: z.string(),
                    })
                    .optional(),
            }),
        }),
        links: z.array(
            z.object({
                rel: z.string(),
                href: z.string().url(),
                method: z.string(),
                encType: z.string().optional(),
            }),
        ),
    })
    .passthrough()
    .partial();

const BillingAccountCardType = z.enum(["creditcard", "paying_by_invoice"]).openapi({ ref: "BillingAccountCardType" });

const BillingAccountCardDataBase = z.object({
    id: z.string(),
    expire_month: int_or_string.optional(),
    expire_year: int_or_string.optional(),
    last4: z.string().optional(),
    brand: z.string().optional(), // TODO: this field needs an example in mock data
    card_holder: z.string().nullable().optional(),
    type: BillingAccountCardType,
    is_verified: z.boolean().optional(),
    is_verification_pending: z.boolean().optional(),
    // TODO: split these up by processor type?
    client: z.object({}).optional(), // for invoice payment
});

const BillingAccountCommonCardData = BillingAccountCardDataBase.extend({
    card_type: z.enum([
        // TODO: The enum should not be discriminated by this field since it can have arbitrary values
        "visa",
        "mastercard",
        "discover",
        "amex",
        "jcb",
        "unionpay",
        "Visa",
        "MasterCard",
        "By invoice",
        "Mock card",
    ]),
    processor_data: StripeProcessorData,
}).openapi({ ref: "BillingAccountCommonCardData" });

const BillingAccountPaypalVaultCardData = BillingAccountCardDataBase.extend({
    card_type: z.literal("PayPal"),
    processor_data: PaypalVaultProcessorData,
}).openapi({ ref: "BillingAccountPaypalVaultCardData" });

export const BillingAccountCardData = z
    .discriminatedUnion("card_type", [BillingAccountCommonCardData, BillingAccountPaypalVaultCardData])
    .openapi({ ref: "BillingAccountCardData" });

const CardProcessorKeyword = z.enum(["omise_creditcard", "stripe_creditcard", "paypal_vault", "invoice"]);

export const CardProcessor = z
    .object({
        id: int,
        is_active: z.boolean(),
        keyword: CardProcessorKeyword,
    })
    .openapi({ ref: "CardProcessor" });

/**
 * NB: !!! Do not confuse with `BillingAccountCardData`
 */
export const BillingAccountCard = z
    .object({
        additional_data: json_string.describe("JSON string containing `BillingAccountCardData`"),
        billing_account_id,
        billing_account_processor_identifier_id: int,
        created: datetime_epoch_s,
        id: int,
        identifier: z.string(),
        is_deleted: z.boolean(),
        is_primary: z.boolean(),
        is_verification_pending: z.boolean(),
        is_verified: z.boolean(),
        processor: CardProcessor.optional(),
        valid_thru: datetime_epoch_s,
    })
    .openapi({ ref: "BillingAccountCard" });

export const RestrictionLevel = z
    .enum(["CLEAR", "LIMITED", "FROZEN", "TERMINATED"])
    .describe("Billing-account restriction state.")
    .openapi({ ref: "RestrictionLevel" });

const PromotionCreditType = z
    .enum(["first_free", "referral_receiver", "signup_campaign"])
    .openapi({ ref: "PromotionCreditType" });

export const BillingAccountEligiblePromotion = z
    .object({
        credit_type: PromotionCreditType,
        amount: z.number(),
        top_up_requirement: z.number().optional(),
        top_up_done: z.number().optional(),
    })
    .openapi({ ref: "BillingAccountEligiblePromotion" });

const BillingAccountBaseFields = z
    .object({
        address_line1: z.string().optional(),
        address_line2: z.string().optional(),
        city: z.string().optional(),
        company_name: z.string().optional(),
        company_reg_code: z.string().optional(),
        company_vat_number: z.string().optional(),
        country: z.string().length(2).optional(),
        state_code: z.string().optional(),
        county: z.string().optional(),
        customer_id_number: z.string().optional(),
        customer_name: z.string().optional(),
        customer_phone: z.string().optional(),
        lang: z
            .union([z.string().min(2).max(3), z.literal("")])
            .optional()
            .openapi({ example: "eng" }),
        post_index: z.string().optional(),
        site: z.string(),
        title: z.string(),
        integration_id: z.string().optional(),
    })
    .openapi({ ref: "BillingAccountBaseFields" });

/** from:
 * https://gitlab.com/warrenio/platform/framework/payment-api/blob/b83aaea3b7886eb933c82522297e6e979ccfa586/qm_payment/models.py#L276-318
 */
export const RunningTotals = z
    .object({
        credit_amount: z.number(),
        credit_available: z.number(),
        charging_subtotal: z.number().optional(),
        charging_subtotal_resource_usage: z.number().optional(),
        discount_amount: z.number(),
        ongoing: z.number(),
        ongoing_negative_since: datetime_epoch_s.optional(),
        subtotal: z.number(),
        subtotal_resource_usage: z.number().optional(),
        total_before_tax: z.number(),
        total: z.number(),
        vat_tax: z.number(),
    })
    .openapi({ ref: "RunningTotals" });

const LowBalanceNoticeSettings = z
    .object({
        threshold: z.number().optional(),
        is_enabled: z.boolean(),
    })
    .openapi({ ref: "LowBalanceNoticeSettings" });

const BillingAccountStatus = z.enum(["ACTIVE", "SUSPENDED", "PENDING_VALIDATION"]).openapi({
    ref: "BillingAccountStatus",
});

export const KycStatus = z.nativeEnum(KycProcessStatusEnum).openapi({ ref: "KycStatus" });
export const DigioKycData = z.object({
    start: z
        .object({
            id: z.string(),
            customer_identifier: z.string(),
            access_token__id: z.string(),
        })
        .optional(),
});
// right now only Digio is supported
export const BillingAccountKycInfo = z
    .object({
        provider: z.enum(["digio"]),
        status: KycStatus,
        created: datetime_epoch_s,
        updated: datetime_epoch_s,
        data: DigioKycData,
    })
    .openapi({ ref: "BillingAccountKycInfo" });

export const BillingAccount = BillingAccountBaseFields.extend({
    additional_data: json_string.optional(),
    advertise_free_credit: z.boolean().optional(),
    allow_debt: z.boolean().describe("`true` means post-payment billing, `false` means pre-payment billing."),
    can_pay: z.boolean(),

    created: datetime_epoch_s,
    credit_amount: z.number(),
    discount_percentage: z.number(),
    eligible_promotions: z.array(BillingAccountEligiblePromotion).optional(),

    id: billing_account_id,

    is_active: z.boolean(),
    is_default: z.boolean(),
    is_deleted: z.boolean(),

    deleted_at: datetime_z.optional(),

    email: z.string(),

    is_recurring_payment_enabled: z.boolean(),

    open_level_override: z.string().optional(),
    paying_by_invoice: z.boolean(),

    precalc_credit_amount: z.number(),
    precalc_days_in_debt: z.number(),
    precalc_ongoing: z.number(),

    primary_card: BillingAccountCardData.optional(),

    recurring_payment_amount: z.number().optional(),
    recurring_payment_threshold: z.number().optional(),

    referral_code: z.string().optional(),
    referral_share_code: z.string(),
    reseller: z.string(),
    restriction_level: RestrictionLevel,
    running_totals: RunningTotals,
    send_invoice_email: z.boolean(),

    status: BillingAccountStatus.optional(), // optional-backwards-compatibility
    suspend_reason: z.string().optional(),
    unpaid_amount: z.number(),
    user_id,
    vat_percentage: z.number(),

    document: z.string().optional(),
    document_name: z.string().optional(),

    low_balance_notice_settings: LowBalanceNoticeSettings,

    kyc_info: BillingAccountKycInfo.optional(),
}).openapi({ ref: "BillingAccount" });

const BillingAccountEditFields = BillingAccountBaseFields.partial().openapi({ ref: "BillingAccountEditFields" });

export const BillingAccountCreateTypeParam = z
    .enum(["invoice", "other"])
    .openapi({ ref: "BillingAccountCreateTypeParam" });

export const BillingAccountCreateFields = BillingAccountEditFields.extend({
    client_email: BillingAccount.shape.email.optional(),
    referral_code: BillingAccount.shape.referral_code.optional(),
    document: BillingAccount.shape.document.optional(),
    document_name: BillingAccount.shape.document_name.optional(),
    type: BillingAccountCreateTypeParam.optional(),
}).openapi({
    ref: "BillingAccountCreateFields",
});

export const BillingAccountUpdateFields = BillingAccountEditFields.extend({
    email: BillingAccount.shape.email.optional(),
})
    .partial()
    .openapi({
        ref: "BillingAccountUpdateFields",
    });

export const BillingListResponse = z.array(BillingAccount);

export const RestrictionLevelClearTopupThreshold = z
    .object({
        restriction_level_clear_topup_threshold: z
            .number()
            .nullable()
            .describe(
                "Total top-up amount required before a LIMITED billing account is automatically promoted to CLEAR. Null disables the automatic promotion threshold.",
            ),
    })
    .openapi({ ref: "RestrictionLevelClearTopupThreshold" });

export const InvoiceTypes = z
    .enum(["cloud_services", "credit_topup", "credit_note"])
    .describe(
        "`cloud_services` represents billed resource usage, `credit_topup` is a credit purchase invoice, and `credit_note` reverts an existing invoice.",
    )
    .openapi({ ref: "InvoiceTypes" });

export const DocumentTypes = z
    .enum(["invoice", "report", "credit_note"])
    .describe(
        "`invoice` is accounting-relevant, `credit_note` reverts an earlier invoice and is accounting-relevant, and `report` is a pre-payment usage report that is not an invoice.",
    )
    .optional()
    .openapi({ ref: "DocumentTypes" });

const InvoiceStatus = z
    .nativeEnum(InvoiceStatusEnum)
    .describe(
        "Invoice status where `0` = creating, `5` = unpaid, and `10` = paid. Pre-payment invoices are always paid.",
    )
    .openapi({ ref: "InvoiceStatus" });

export const InvoiceId = z.union([z.literal("current"), z.number()]).openapi({ ref: "InvoiceId" });

const InvoiceRecord = z
    .object({
        amount: z.number(),
        created: datetime_epoch_s,
        descr: z.string().optional(),
        id: int,
        invoice_id: int,
        is_technical: z.boolean(),
        item_price: z.number(),
        location_slug: z.string().optional(),
        name: z.string(),
        qty_unit: z.string(),
        qty: z.number(),
        vat_amount: z.number(),
        discount_amount: z.number().optional(),
        discount_percentage: z
            .number()
            .min(0)
            .max(100)
            .describe("In case of record level discounts, discount percentage applied for this record.")
            .optional(),
    })
    .openapi({ ref: "InvoiceRecord" });

export const BillingAccountUsage = InvoiceRecord.omit({ created: true }).openapi({ ref: "BillingAccountUsage" });
export const BillingUsageListResponse = z.array(BillingAccountUsage);

export const BillingAccountInvoice = z
    .object({
        id: InvoiceId,
        is_current: z.boolean().optional(),
        name: z.string().optional(),
        account_snapshot: json_string.optional(),
        billing_account_id,
        created: datetime_epoch_s,
        discount_percentage: z
            .number()
            .min(0)
            .max(100)
            .describe(
                "Discount percentage used to calculate this invoice. Used for legacy, invoice level discounts only.",
            )
            .optional(),
        has_record_level_discount: z.boolean(),
        due_date: datetime_epoch_s,
        padded_id: z
            .string()
            .regex(/\d+/)
            .describe("Deprecated legacy document identifier. Use `document_number` instead.")
            .optional(),
        document_number: z
            .string()
            .describe("Human-facing document number with a type-specific prefix such as `#`, `CN-`, or `RP-`.")
            .optional(),
        commitment_uuid: uuid.optional(),
        period_end: datetime_epoch_s,
        period_start: datetime_epoch_s,
        precalc_credit: z.number().optional(),
        precalc_discount_amount: z.number().optional(),
        precalc_subtotal: z.number().optional(),
        precalc_total: z.number().optional(),
        precalc_total_before_tax: z.number().optional(),
        precalc_vat_tax: z.number().optional(),
        records_list: z.array(InvoiceRecord),
        status: InvoiceStatus,
        totals: z.object({
            subtotal_resource_usage: z
                .number()
                .describe(
                    "Sum of actual resource-usage rows only, excluding overdue unpaid rows and credit top-ups, before discount, credit, and VAT adjustments.",
                )
                .optional(),
            subtotal: z.number().describe("Sum of all invoice rows before discount, credit, and VAT adjustments."),
            discount_amount: z.number().describe("Actual discount amount deducted from the subtotal."),
            credit: z
                .number()
                .describe(
                    "For post-payment, credit drawn from remaining account credit. For pre-payment, this reflects actual period expense after discount.",
                ),
            total_before_tax: z
                .number()
                .describe(
                    "For post-payment, amount due before VAT. For pre-payment, this is always `0` and has no accounting meaning.",
                ),
            vat_tax: z.number().describe("VAT amount added to the invoice total."),
            total: z
                .number()
                .describe(
                    "For post-payment, final total calculated as subtotal - discount - credit + VAT. For pre-payment, this is always `0`.",
                ),
        }),
        transaction_list: z
            .array(
                z.object({
                    additional_data: json_string.optional(),
                    amount: z.number(),
                    created: int,
                    id: int,
                    identifier: z.string().optional(),
                    is_success: z.boolean(),
                    payment_object_id: int.optional(),
                }),
            )
            .optional(),
        type: InvoiceTypes,
        document_type: DocumentTypes,
        vat_percentage: z.number().describe("VAT percentage used for this invoice or report."),
        vat_components: z
            .array(z.object({ name: z.string(), amount: z.number() }))
            .describe("VAT components if VAT consists of multiple parts.")
            .optional(),
    })
    .openapi({ ref: "BillingAccountInvoice" });

export const BillingInvoicesListResponse = z.array(BillingAccountInvoice);

export const BillingAccountHistory = z
    .object({
        acting_user_id: user_id.optional(),
        amount: z.number(),
        billing_account_id,
        created: datetime_epoch_s,
        description: z.string(),
        id: int,
        reference: z.string().optional(),
    })
    .openapi({ ref: "BillingAccountHistory" });

export const BillingHistoryListResponse = z.array(BillingAccountHistory);

//#region Payment methods management & actual payment / top-up process

export const SaveMethodsBody = z
    .object({
        billing_account_id,
        method_ids: z.array(LinkMethodId),
    })
    .openapi({ ref: "SaveMethodsBody" });

export const AddCardBody = z
    .object({
        billing_account_id,
        token: z.string(),
        processor_keyword: CardProcessorKeyword,
        session_id: z.string().optional(), // used in PayPal Vault processor
    })
    .openapi({ ref: "AddCardBody" });

export const ApplyForInvoiceBody = z
    .object({
        billing_account_id,
    })
    .openapi({ ref: "ApplyForInvoiceBody" });

const CardPrehookBody = z.object({ billing_account_id, processor_keyword: CardProcessorKeyword });

const CardPrehookResponse = z
    .object({
        id: z.string(),
        client_secret: z.string(),
        status: z.string(),
        usage: z.string(),
    })
    // Lots of unknown fields here, so we allow them
    .passthrough()
    .openapi({ ref: "CardPrehookResponse" });

const CardVerifyResponse = z
    .object({
        card: BillingAccountCard,
        authorize_uri: z.string().url().optional(),
        data_ref: z.string().optional(),
    })
    .openapi({ ref: "CardVerifyResponse" });

export const CardVerifyRequest = z
    .object({ payment_object_id: int, front_base_url: z.string(), force_3ds: z.boolean() })
    .openapi({ ref: "CardVerifyRequest" });

//#region "/payment/link/send_data"
const LinkParamsBase = z
    .object({
        referrer_url: z.string().url(),
    })
    .openapi({ ref: "LinkParamsBase" });

const LinkParamsGeneric = LinkParamsBase.extend({
    paymentMethod: z.string(),
}).openapi({ ref: "LinkParamsGeneric" });

const LinkParamsOmise = LinkParamsGeneric.extend({
    source_id: z.string(),
    currency: z.string(),
}).openapi({ ref: "LinkParamsOmise" });

const LinkParams = z.union([LinkParamsOmise, LinkParamsGeneric]).openapi({ ref: "LinkParams" });

// NB: Since these types are not used as part of the OpenAPI request/response specs, we have to explicitly enumerate
// them so they would still be exported.
export const allLinkParamSchemas: Record<string, ZodTypeAny> = { LinkParams };

const PaymentCreditTarget = z
    .object({
        type: z.literal("credit"),
        amount: z.number(),
    })
    .openapi({ ref: "PaymentCreditTarget" });

const PaymentInvoiceTarget = z
    .object({
        type: z.literal("invoice"),
        invoice_id: int,
    })
    .openapi({ ref: "PaymentInvoiceTarget" });

export const PaymentTarget = z
    .discriminatedUnion("type", [PaymentCreditTarget, PaymentInvoiceTarget])
    .openapi({ ref: "PaymentTarget" });

export const LinkProcessorId = z
    .enum([
        "stripe",
        "stripe_wallet",
        "omise",
        "duitku",
        "sslcommerz",
        "adyen",
        "clictopay",
        "coingate",
        "razorpay",
        "paypal_checkout",
        "evc_hormuud",
    ])
    .openapi({ ref: "LinkProcessorId" });

export const LinkSendDataBody = z
    .object({
        billing_account_id,
        params: json_string.describe("JSON string containing `LinkParams*` types"),
        processor: LinkProcessorId,
        target: PaymentTarget,
    })
    .openapi({
        ref: "LinkSendDataBody",
    });

// TODO: Add missing fields to these
const StripeSendDataResponse = z
    .object({
        front_redirect_method: z.string(),
        client_secret: z.string(),
        return_url: z.string().url(),
    })
    .openapi({ ref: "StripeSendDataResponse" });

const OmiseSendDataResponseBase = z
    .object({
        charge_id: z.string(),
        charge_status: z.string(),
        flow: z.string(),
        persistent_data_ref: z.string(),
    })
    .openapi({ ref: "OmiseSendDataBase" });

const OmiseRedirectSendDataResponse = OmiseSendDataResponseBase.extend({
    redirect_url: z.array(z.string().url()),
}).openapi({ ref: "OmiseRedirectSendDataResponse" });

const OmiseQrSendDataResponse = OmiseSendDataResponseBase.extend({
    qrcode_url: z.string().url(),
}).openapi({ ref: "OmiseQrSendDataResponse" });

// https://docs.duitku.com/api/en/#response-parameters
export const DuitkuRedirectSendDataResponse = z
    .object({
        redirect_url: z.string().url(),
        link_response: z.object({
            merchantCode: z.string(),
            reference: z.string(),
            paymentUrl: z.string(),
            vaNumber: z.string(),
            amount: z.number(),
            qrString: z.string(),
        }),
    })
    .openapi({ ref: "DuitkuRedirectSendDataResponse" });

const CommonLinkRedirectSendDataResponse = z.object({
    redirect_url: z.string().url(),
    persistent_data_ref: z.string(),
});

export const SslcommerzRedirectSendDataResponse = CommonLinkRedirectSendDataResponse.extend({}).openapi({
    ref: "SslcommerzRedirectSendDataResponse",
});

export const ClictopayRedirectSendDataResponse = CommonLinkRedirectSendDataResponse.extend({}).openapi({
    ref: "ClictopayRedirectSendDataResponse",
});

export const CoingateRedirectSendDataResponse = CommonLinkRedirectSendDataResponse.extend({}).openapi({
    ref: "CoingateRedirectSendDataResponse",
});

export const PaypalCheckoutRedirectSendDataResponse = CommonLinkRedirectSendDataResponse.extend({}).openapi({
    ref: "PaypalCheckoutRedirectSendDataResponse",
});

export const RazorpaySendDataResponse = z
    .object({
        persistent_data_ref: z.string(),
        title: z.string(),
        amount: z.number(),
        order_id: z.string(),
    })
    .openapi({ ref: "RazorpaySendDataResponse" });

export const StripeWalletRedirectSendDataResponse = z
    .object({
        front_redirect_method: z.string(),
        client_secret: z.string(),
        return_url: z.string(),
        pay_intent_id: z.string(),
    })
    .openapi({ ref: "StripeWalletRedirectSendDataResponse" });

const GenericRedirectSendDataResponse = z
    .object({
        redirect_url: z.string().url(),
        link_response: z.unknown(),
    })
    .openapi({ ref: "GenericRedirectSendDataResponse" });

const LinkSendDataResponse = z
    .object({
        success: z.boolean(),
        error_msg: z.string(),
        data: z.union([
            StripeSendDataResponse,
            OmiseRedirectSendDataResponse,
            OmiseQrSendDataResponse,
            DuitkuRedirectSendDataResponse,
            RazorpaySendDataResponse,
            SslcommerzRedirectSendDataResponse,
            ClictopayRedirectSendDataResponse,
            CoingateRedirectSendDataResponse,
            PaypalCheckoutRedirectSendDataResponse,
            StripeWalletRedirectSendDataResponse,
            GenericRedirectSendDataResponse,
        ]),
    })
    .openapi({ ref: "LinkSendDataResponse" });
//#endregion

//#region "/payment/credit/buy"
const GenericCreditBuyBodyBase = z.object({
    billing_account_id,
    payment_object_id: int.describe("ID of the card"),
    on_session: z.boolean(),
    target: PaymentTarget,
});

const GenericCreditBuyBody = GenericCreditBuyBodyBase.openapi({
    ref: "GenericCreditBuyBody",
});

export const OmiseCreditBuyBody = GenericCreditBuyBodyBase.extend({
    front_base_url: z.string(),
    force_3ds: z.boolean(), // PS! temporary param to force 3DS even when old UI is not using it, remove when old UI no longer exists (refactor also payment's backend code)
}).openapi({
    ref: "OmiseCreditBuyBody",
});

const PaypalVaultCreditBuyBody = GenericCreditBuyBodyBase.extend({
    session_id: z.string().optional(),
}).openapi({
    ref: "PaypalVaultCreditBuyBody",
});

export const CreditBuyBody = z
    .union([GenericCreditBuyBody, OmiseCreditBuyBody, PaypalVaultCreditBuyBody])
    .openapi({ ref: "CreditBuyBody" });

const CreditBuyResponseBase = z
    .object({
        success: z.boolean(),
        message: z.string(),
        data: z.unknown(),
        relay_props: z.object({}),
    })
    .openapi({ ref: "CreditBuyResponseBase" });

const StripeCreditBuyResponse = CreditBuyResponseBase.extend({
    data: z
        .object({
            client_secret: z.string(),
            payment_method: z.string(),

            amount: z.number(),
            status: z.string(),
        })
        // NB: Allow unknown fields for future compatibility
        .passthrough(),
}).openapi({ ref: "StripeCreditBuyResponse" });

const OmiseCreditBuyResponse = CreditBuyResponseBase.extend({
    data: z
        .object({
            id: z.string(),
            object: z.string(),
            status: z.string(),
        })
        .passthrough(),
    relay_props:
        // This is either an empty object or both of these fields, but TypeScript can not represent that nicely so just
        // use `.optional()`.
        z.object({
            authorize_uri: z.string().url().optional(),
            data_ref: z.string().optional(),
        }),
}).openapi({ ref: "OmiseCreditBuyResponse" });

const PayPalVaultCreditBuyResponse = CreditBuyResponseBase.extend({
    data: PaypalVaultProcessorData,
}).openapi({ ref: "PayPalVaultCreditBuyResponse" });

const CreditBuyResponse = z
    .union([StripeCreditBuyResponse, OmiseCreditBuyResponse, PayPalVaultCreditBuyResponse])
    .openapi({ ref: "CreditBuyResponse" });

const CreditBuyPosthookBody = z
    .object({
        billing_account_id,
        charge_id: z.string(),
        payment_object_id: int,
    })
    .openapi({ ref: "CreditBuyPosthookBody" });

const processor_name = z.string().openapi({ ref: "processor_name" });
const topup_amount = z.number().openapi({ ref: "topup_amount" });

const FinalAmountResponse = z
    .object({
        final_amount: z.number(),
    })
    .openapi({ ref: "FinalAmountResponse" });
//#endregion

const persistent_identifier = z.string().openapi({ ref: "persistent_identifier" });

const PaymentResultResponse = z
    .object({
        status: z.string(),
        message: z.string().optional(),
    })
    .openapi({ ref: "PaymentResultResponse" });

const PersistentDataResponse = z
    .object({
        billing_account_id,
        created: datetime_epoch_s,
        id: int,
        identifier: persistent_identifier,
        processor_method: ProcessorMethodId,
        // XXX: optional - Omise processor seems to not send this in the beginning
        status: z.enum(["pending", "success", "failed"]).optional(),
        data: json_string,
    })
    .openapi({ ref: "PersistentDataResponse" });

export const AutomatedTopUpEnableDisableBody = z
    .union([
        z.object({ is_recurring_payment_enabled: z.literal(false) }),
        z.object({
            recurring_payment_threshold: z.number(),
            recurring_payment_amount: z.number(),
            is_recurring_payment_enabled: z.literal(true),
        }),
    ])
    .openapi({ ref: "AutomatedTopUpEnableDisableBody" });

const AutomatedTopUpEnableDisableResponse = z
    .object({
        account: BillingAccount,
        payment_report: z
            .object({
                errors: z.array(z.unknown()),
                triggered: z.boolean(),
            })
            .optional(),
    })
    .openapi({ ref: "AutomatedTopUpEnableDisableResponse" });

export const ModifyLowBalanceNoticeSettingsBody = z
    .object({
        billing_account_id,
        threshold: z.number(),
        is_enabled: z.boolean(),
    })
    .openapi({ ref: "ModifyLowBalanceNoticeSettingsBody" });

export const ModifyLowBalanceNoticeSettingsResponse = LowBalanceNoticeSettings.openapi({
    ref: "ModifyLowBalanceNoticeSettingsResponse",
});

export const StartKycProcessBody = z
    .object({
        billing_account_id,
    })
    .openapi({ ref: "StartKycProcessBody" });

const StartDigioKycProcessResponse = BillingAccountKycInfo.omit({
    provider: true,
    created: true,
    updated: true,
}).openapi({
    ref: "StartDigioKycProcessResponse",
});
//#endregion

const CampaignActivateResponse = z.object({ message: z.string() }).openapi({ ref: "CampaignActivateResponse" });

export const PaypalSetupTokenResponse = z
    .object({
        token: z.string(),
    })
    .openapi({ ref: "PaypalSetupTokenResponse" });

export const PaypalTokenBaseResponse = z.object({
    token: z.string(),
});

export const PaypalPaymentTokenResponse = PaypalTokenBaseResponse.openapi({
    ref: "PaypalPaymentTokenResponse",
});

export const PaypalIdTokenResponse = PaypalTokenBaseResponse.openapi({
    ref: "PaypalIdTokenResponse",
});

export const invoicePdfResponse: ZodOpenApiResponseObject = {
    description: "PDF invoice content",
    content: { "application/pdf": {} },
};

export const CountryRegionsConfigResponse = z
    .record(
        z.string().regex(/^[A-Z]{2}$/), // country code: ISO 3166-1 alpha-2
        z.object({
            states: z.array(
                z.object({
                    code: z.string(),
                    name: z.string(),
                }),
            ),
        }),
    )
    .openapi({ ref: "CountryRegionsConfigResponse" });

export const billing_account_id_query = billing_account_id.openapi({
    param: { name: "billing_account_id", in: "query" },
});

const billing_account_id_path = billing_account_id.openapi({
    param: { name: "id", in: "path" },
});

const payment_object_id_query = int.openapi({ param: { name: "payment_object_id", in: "query" } });

const campaign_code_path = z.string().openapi({ param: { name: "campaign_code", in: "path" } });

const paypal_setup_token_query = z.string().openapi({ param: { name: "setup_token", in: "query" } });

const paypal_session_id_query = z.string().openapi({ param: { name: "session_id", in: "query" } });

const invoice_id_query = InvoiceId.openapi({ param: { name: "invoice_id", in: "query" } });

export const billingPaths = tagPaths("billing")({
    "/payment/billing_account/list": {
        get: {
            summary: "List billing accounts",
            description: ind`
            List billing accounts attached to user.

            The \`show_shadow\` parameter can be set to also include deleted accounts.`,
            responses: {
                ...successResponse(BillingListResponse),
            },
        },
    },
    "/payment/billing_account": {
        post: {
            summary: "Create billing account",
            requestBody: formBody(BillingAccountCreateFields),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
        put: {
            summary: "Update billing account",
            description: ind`
            NB: All existing data fields that billing account already has must be passed along. Otherwise missing fields will be deleted (except the e-mail field).

            Recurring payment configuration update is handled by another endpoint.`,
            parameters: [billing_account_id_query],
            requestBody: formBody(BillingAccountUpdateFields),
            responses: {
                ...successResponse(BillingAccount),
            },
        },
        delete: {
            summary: "Delete billing account",
            parameters: [billing_account_id_query],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/billing_account/set_default": {
        post: {
            summary: "Set as default",
            description: "Set billing account as default",
            parameters: [billing_account_id_query],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/billing_account/{id}/campaign_activate/{campaign_code}": {
        post: {
            summary: "Activate campaign code",
            parameters: [billing_account_id_path, campaign_code_path],
            responses: {
                ...successResponse(CampaignActivateResponse),
                ...errorResponse("Invalid campaign code."),
            },
        },
    },
    "/payment/billing_account/{id}/recurring_payment": {
        put: {
            summary: "Enable or disable automated top up",
            parameters: [billing_account_id_path],
            requestBody: formBody(AutomatedTopUpEnableDisableBody),
            responses: {
                ...successResponse(AutomatedTopUpEnableDisableResponse),
            },
        },
    },
    "/payment/low_balance_notice_settings": {
        put: {
            summary: "Modify low balance notification settings",
            requestBody: formBody(ModifyLowBalanceNoticeSettingsBody),
            responses: {
                ...successResponse(ModifyLowBalanceNoticeSettingsResponse),
            },
        },
    },
    "/config/payment/restriction_level_clear_topup_threshold": {
        get: {
            summary: "Get topup threshold for Clear restriction level",
            responses: {
                ...successResponse(RestrictionLevelClearTopupThreshold),
            },
        },
        put: {
            summary: `Update topup threshold for Clear restriction level ${privateApi}`,
            requestBody: jsonBody(unref(RestrictionLevelClearTopupThreshold).partial()),
            responses: {
                ...successResponse(RestrictionLevelClearTopupThreshold),
            },
        },
    },
    "/payment/usage": {
        get: {
            summary: "List usage",
            description: "Resources, prices and costs for the specified billing account, for the current month.",
            parameters: [billing_account_id_query],
            responses: {
                ...successResponse(BillingUsageListResponse),
            },
        },
    },
    "/payment/invoice/list": {
        get: {
            summary: "List invoices",
            description: "List billing account's invoices.",
            parameters: [billing_account_id_query],
            responses: {
                ...successResponse(BillingInvoicesListResponse),
            },
        },
    },
    "/payment/invoice": {
        get: {
            summary: "Get invoice",
            description: "Get an invoice by ID.",
            parameters: [invoice_id_query],
            responses: {
                ...successResponse(BillingAccountInvoice),
            },
        },
    },
    "/payment/invoice/pdf": {
        get: {
            summary: "Download invoice PDF",
            parameters: [invoice_id_query],
            responses: {
                200: invoicePdfResponse,
            },
        },
    },
    "/payment/paypal/id_token": {
        get: {
            summary: `Get PayPal id token ${privateApi}`,
            parameters: [paypal_session_id_query],
            responses: {
                ...successResponse(PaypalIdTokenResponse),
            },
        },
    },
    "/payment/paypal/setup_token": {
        get: {
            summary: `Get PayPal setup token ${privateApi}`,
            parameters: [paypal_session_id_query, z.string().openapi({ param: { name: "return_url", in: "query" } })],
            responses: {
                ...successResponse(PaypalSetupTokenResponse),
            },
        },
    },
    "/payment/paypal/payment_token": {
        get: {
            summary: `Get PayPal payment token ${privateApi}`,
            parameters: [paypal_session_id_query, paypal_setup_token_query],
            responses: {
                ...successResponse(PaypalPaymentTokenResponse),
            },
        },
    },
    "/payment/card/list": {
        get: {
            summary: "List credit cards",
            description: "Get list of credit cards attached to billing account.",
            parameters: [billing_account_id_query],
            responses: {
                ...successResponse(z.array(BillingAccountCard)),
            },
        },
    },
    "/payment/card": {
        post: {
            // NB: This is a private API since the user can not complete card verification (3DS etc.) from the public API.
            summary: `Add credit card ${privateApi}`,
            requestBody: jsonBody(AddCardBody),
            responses: {
                ...successResponse(BillingAccountCard),
            },
        },
        delete: {
            summary: "Delete credit card",
            parameters: [payment_object_id_query],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/apply_for_invoice_payment": {
        post: {
            summary: "Apply for invoice payment",
            description:
                "Adds and applies for a 'paying by invoice' payment method. The method will be in a pending state until an administrator verifies it.",
            requestBody: jsonBody(ApplyForInvoiceBody),
            responses: {
                ...successResponse(BillingAccountCard), // invoice payment is also stored as payment object in db (like credit cards)
            },
        },
    },
    "/payment/card/set_primary": {
        put: {
            summary: "Set card as primary",
            description: "Set credit card specified by id as 'primary'. Card must have been verified.",
            parameters: [payment_object_id_query],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/card/prehook": {
        post: {
            summary: `Pre-hook for card addition ${privateApi}`,
            requestBody: jsonBody(CardPrehookBody),
            responses: {
                ...successResponse(CardPrehookResponse),
            },
        },
    },
    "/payment/card/verify": {
        post: {
            summary: `Verify card ${privateApi}`,
            requestBody: jsonBody(CardVerifyRequest),
            responses: {
                ...successResponse(CardVerifyResponse),
            },
        },
    },
    "/payment/credit/list": {
        get: {
            summary: "List credit history",
            description: "Billing account's credit history records.",
            parameters: [billing_account_id_query],
            responses: {
                ...successResponse(BillingHistoryListResponse),
            },
        },
    },
    "/payment/link/save_methods": {
        post: {
            summary: `Set payment methods ${privateApi}`,
            requestBody: jsonBody(SaveMethodsBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/link/send_data": {
        post: {
            summary: `Start top-up process ${privateApi}`,
            requestBody: jsonBody(LinkSendDataBody),
            responses: {
                ...successResponse(LinkSendDataResponse),
                ...errorResponse("Payment channel not available"),
            },
        },
    },
    "/payment/link/process_result": {
        get: {
            summary: `Poll for payment result ${privateApi}`,
            parameters: [
                billing_account_id_query,
                persistent_identifier.openapi({ param: { name: "identifier", in: "query" } }),
            ],
            responses: {
                ...successResponse(PaymentResultResponse),
            },
        },
    },
    "/payment/persistent_data": {
        get: {
            summary: `Poll for webhook responses ${privateApi}`,
            parameters: [
                billing_account_id_query,
                persistent_identifier.openapi({ param: { name: "identifier", in: "query" } }),
            ],
            responses: {
                ...successResponse(PersistentDataResponse),
            },
        },
    },
    "/payment/final_topup_amount": {
        get: {
            summary: `Get final topup amount ${privateApi}`,
            parameters: [
                billing_account_id_query,
                processor_name.openapi({ param: { name: "processor", in: "query" } }),
                topup_amount.openapi({ param: { name: "amount", in: "query" } }),
            ],
            responses: {
                ...successResponse(FinalAmountResponse),
            },
        },
    },
    "/payment/credit/buy": {
        post: {
            summary: `Start credit purchase process ${privateApi}`,
            requestBody: jsonBody(CreditBuyBody),
            responses: {
                ...successResponse(CreditBuyResponse),
            },
        },
    },
    "/payment/credit/buy/posthook": {
        post: {
            summary: `Post-hook for credit purchase ${privateApi}`,
            requestBody: jsonBody(CreditBuyPosthookBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/payment/kyc/start": {
        post: {
            summary: `Start KYC process ${privateApi}`,
            requestBody: jsonBody(StartKycProcessBody),
            responses: {
                ...successResponse(StartDigioKycProcessResponse), // how to solve multiple possible return types (other providers than "Digio")?
            },
        },
    },
    "/payment/config/country_regions": {
        get: {
            summary: `Get country and region config ${privateApi}`,
            responses: {
                ...successResponse(CountryRegionsConfigResponse),
            },
        },
    },
});
